import * as React from "react";
import { useDebouncedCallback } from "use-debounce";

import { UnitsLayout } from "../UnitsLayout/UnitsLayout";
import { IUnitRegistry } from "../UnitRegistry/IUnitRegistry";
import { UnitId, unitWithoutInstance } from "../../Unit/UnitId/UnitId";
import { IUnitNavigator } from "../IUnitNavigator";
import { useWindowEventListener } from "../../../Commons/DOM/UseWindowEventListener";
import { delay } from "../../../../Engine/utils";
import { isElementInViewPort, iterateOverHTMLCollection } from "../../../Commons/DOM/DomHelpers";
import { schedulerWrapper, TaskPriority } from "../../../../Helpers/Scheduler/SchedulerApi";

import { FirstRenderContext, PartialRenderFirstRenderContextObject } from "./PartiallyRenderedUnit";

interface UnitPartialRenderManagerProps {
    children?: React.ReactNode;
    maxUnitCount: number;
    unitRegistry: IUnitRegistry;
}

export const UnitPartialRenderer = React.forwardRef(
    (props: UnitPartialRenderManagerProps, ref: React.ForwardedRef<IUnitNavigator>) => {
        const partialRenderFirstRenderContextRef = React.useRef<FirstRenderContext>({
            firstRenderCompleted: false,
            renderedAtFirstCount: props.maxUnitCount,
        });

        const root = React.createRef<HTMLDivElement>();

        React.useImperativeHandle(
            ref,
            () => ({
                scrollToUnit: async (target: UnitId) => {
                    // note: чтобы дождаться закрытия модалки
                    await delay(0);
                    if (target.instance != undefined) {
                        const targetParent = props.unitRegistry.getUnitController(unitWithoutInstance(target));
                        targetParent?.ensureUnitAvailableForScroll(target);
                        const targetUnit = props.unitRegistry.getUnitController(target);
                        targetUnit?.scrollIntoView();
                    } else {
                        const targetUnit = props.unitRegistry.getUnitController(target);
                        targetUnit?.scrollIntoView();
                    }
                },
            }),
            [props.unitRegistry]
        );

        React.useLayoutEffect(() => {
            partialRenderFirstRenderContextRef.current.firstRenderCompleted = true;
        }, []);

        const recalculatedDrawingState = () => {
            schedulerWrapper(
                () => {
                    if (root.current == undefined) {
                        return;
                    }
                    let foundMiddleElement = false;
                    let foundBottomElement = false;
                    let elementsAfterMiddle = 0;
                    const maxUnitCountWindow: Element[] = [];
                    // NOTE "Хитрый" алгоритм, который хочет за один проход по элементам сообщить им их состояние (рисоваться или нет) и
                    // при этом не хранить большой массив элементов.
                    for (const child of iterateOverHTMLCollection(root.current.children)) {
                        if (!foundMiddleElement) {
                            if (maxUnitCountWindow.push(child) > props.maxUnitCount) {
                                const notRenderedChild = maxUnitCountWindow.shift();
                                if (notRenderedChild != undefined) {
                                    // @ts-ignore
                                    notRenderedChild["__candyPartialLoadHandler"]?.current.changeStateHandler(false);
                                }
                            }
                            if (isElementInViewPort(child)) {
                                foundMiddleElement = true;
                            }
                        } else if (!foundBottomElement) {
                            elementsAfterMiddle++;
                            if (maxUnitCountWindow.push(child) >= props.maxUnitCount) {
                                if (elementsAfterMiddle >= props.maxUnitCount / 2) {
                                    foundBottomElement = true;
                                    for (const renderedChild of maxUnitCountWindow) {
                                        // @ts-ignore
                                        renderedChild["__candyPartialLoadHandler"]?.current.changeStateHandler(true);
                                    }
                                } else {
                                    const notRenderedChild = maxUnitCountWindow.shift();
                                    if (notRenderedChild != undefined) {
                                        // @ts-ignore
                                        notRenderedChild["__candyPartialLoadHandler"]?.current.changeStateHandler(
                                            false
                                        );
                                    }
                                }
                            }
                        } else {
                            // @ts-ignore
                            child["__candyPartialLoadHandler"]?.current.changeStateHandler(false);
                        }
                    }
                    if (!foundBottomElement) {
                        for (const renderedChild of maxUnitCountWindow) {
                            // @ts-ignore
                            renderedChild["__candyPartialLoadHandler"]?.current.changeStateHandler(true);
                        }
                    }
                },
                { priority: TaskPriority.Background }
            );
        };

        useWindowEventListener("scroll", React.useCallback(useDebouncedCallback(recalculatedDrawingState), []));
        return (
            <UnitsLayout ref={root}>
                <PartialRenderFirstRenderContextObject.Provider value={partialRenderFirstRenderContextRef.current}>
                    {props.children}
                </PartialRenderFirstRenderContextObject.Provider>
            </UnitsLayout>
        );
    }
);
UnitPartialRenderer.displayName = "UnitPartialRenderer";
